import * as Sentry from '@sentry/react'
import type { Session } from '@supabase/supabase-js'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useRouter } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { adminApi } from '~/entities/session/api/admin'
import { authApi } from '~/entities/session/api/auth'
import {
  type ConfirmationLinkSession,
  readConfirmationLink,
} from '~/entities/session/lib/confirmation-link'
import { useRoleAuth } from '~/entities/session/model/use-role-auth'
import * as m from '~/paraglide/messages'
import { extractApiError, getErrorMessageFromError } from '~/shared/lib/api-errors'
import { QueryKey, queryKey } from '~/shared/lib/query-keys'
import { storageManager } from '~/shared/lib/storage-manager'
import { supabase } from '~/shared/lib/supabase'
import { MINUTES } from '~/shared/lib/time-constants'
import { AUTH_BOOT_TIMEOUT_MS } from '~/shared/lib/with-timeout'
import type { BusinessUser, LoginInput, RegisterInput } from '~/shared/types'
import type { AdminUser } from '~/shared/types/admin'

export function useSupabaseSession(): Session | null | undefined {
  const [session, setSession] = useState<Session | null | undefined>(undefined)

  useEffect(() => {
    // `done` guards only the INITIAL resolution: whichever of getSession, the
    // first auth event, or the deadline lands first establishes the value. The
    // deadline exists so a persisted session that can't be read/refreshed (dead
    // project, corrupt token) resolves to logged-out instead of leaving the app
    // pinned on `undefined` - the state that keeps auth pages on a blank gate.
    let done = false
    const timer = setTimeout(() => {
      if (!done) {
        done = true
        setSession(null)
      }
    }, AUTH_BOOT_TIMEOUT_MS)

    supabase.auth
      .getSession()
      .then(({ data }) => {
        if (!done) {
          done = true
          clearTimeout(timer)
          setSession(data.session)
        }
      })
      .catch(() => {
        if (!done) {
          done = true
          clearTimeout(timer)
          setSession(null)
        }
      })

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, next) => {
      done = true
      clearTimeout(timer)
      setSession(next)
    })
    return () => {
      done = true
      clearTimeout(timer)
      subscription.unsubscribe()
    }
  }, [])

  return session
}

const DASHBOARD_ROLES = ['business_admin', 'business_worker', 'global_admin']

/**
 * Whether the session's JWT already grants dashboard access. Read straight from
 * `app_metadata.roles` so business-data queries don't fire (and 403) on the auth
 * pages, where the account has only the `user` role.
 */
export function sessionHasDashboardRole(session: Session | null | undefined): boolean {
  const roles = (session?.user?.app_metadata?.roles as string[] | undefined) ?? []
  return roles.some((r) => DASHBOARD_ROLES.includes(r))
}

/** Whether the session's JWT grants backoffice (admin portal) access. */
export function sessionIsGlobalAdmin(session: Session | null | undefined): boolean {
  const roles = (session?.user?.app_metadata?.roles as string[] | undefined) ?? []
  return roles.includes('global_admin')
}

type LoginDestination =
  | { kind: 'admin'; admin: AdminUser }
  | { kind: 'dashboard'; user: BusinessUser }
  | { kind: 'upgrade' }

interface UseLoginOptions {
  /** Admin portal: reject anyone who does not resolve to the backoffice. */
  requireAdmin?: boolean
}

/**
 * Derives where a fresh session belongs purely from its JWT roles - a
 * global_admin lands in the backoffice, a business account on the dashboard, a
 * bare personal account on the upgrade flow - so no caller hardcodes a route.
 */
async function loginDestination(options: UseLoginOptions): Promise<LoginDestination> {
  // The JWT already carries the role set, so fetch the matching "me" instead
  // of assuming a business account: a global_admin has no business profile and
  // would otherwise 403 on business me and be misrouted to /upgrade.
  const {
    data: { session },
  } = await supabase.auth.getSession()

  if (sessionIsGlobalAdmin(session)) {
    storageManager.setIsAdmin(true)
    storageManager.clearAdminViewingContext()
    const admin = await adminApi.getCurrentAdmin()
    return { kind: 'admin', admin }
  }

  // The admin portal is a closed door - a non-admin never walks out of it
  // holding a business session.
  if (options.requireAdmin) {
    // signOut → SIGNED_OUT → SessionGate clears app keys.
    await supabase.auth.signOut({ scope: 'local' })
    throw new Error(m.adminAccessRequired())
  }

  storageManager.setIsAdmin(false)
  storageManager.clearAdminViewingContext()

  try {
    const user = await authApi.getCurrentUser()
    storageManager.setBusinessId(user.businessId)
    return { kind: 'dashboard', user }
  } catch (error) {
    // Personal account with a valid session but no business role: keep the
    // session (it is the upgrade credential) and route to /upgrade.
    if (extractApiError(error).errorCode === 'BusinessRoleRequired') {
      return { kind: 'upgrade' }
    }
    throw error
  }
}

function useEnterDestination() {
  const queryClient = useQueryClient()
  const router = useRouter()

  return (destination: LoginDestination, greeting: { title: string; description: string }) => {
    if (destination.kind === 'upgrade') {
      router.navigate({ to: '/upgrade' })
      return
    }

    // Seed the role's session query with the "me" already fetched so the target
    // route resolves without a second round-trip.
    if (destination.kind === 'admin') {
      queryClient.setQueryData(queryKey(QueryKey.ADMIN_CURRENT_USER), destination.admin)
      Sentry.setUser({ id: destination.admin.userId, email: destination.admin.email })
    } else {
      queryClient.setQueryData(queryKey(QueryKey.AUTH_CURRENT_USER), destination.user)
      Sentry.setUser({ id: destination.user.userId, email: destination.user.email })
    }

    toast.success(greeting.title, { description: greeting.description })

    router.navigate({ to: destination.kind === 'admin' ? '/admin' : '/dashboard' })
  }
}

/** Shared email/password login for both the business and the admin entry points. */
export function useLogin(options: UseLoginOptions = {}) {
  const enterDestination = useEnterDestination()

  return useMutation({
    mutationFn: async (credentials: LoginInput): Promise<LoginDestination> => {
      await authApi.login(credentials)
      return loginDestination(options)
    },
    onSuccess: (destination) => {
      enterDestination(destination, {
        title: m.welcomeBack(),
        description: m.successfullySignedIn(),
      })
    },
    onError: (error) => {
      toast.error(getErrorMessageFromError(error))
    },
  })
}

/**
 * Signs in with the session an email confirmation link hands back, so confirming
 * the address lands on the dashboard instead of the login form. Returns true
 * while that sign-in is still running.
 */
export function useConfirmationLinkLogin(): boolean {
  const enterDestination = useEnterDestination()
  const [link] = useState(() => readConfirmationLink(window.location.hash))
  const started = useRef(false)

  const adopt = useMutation({
    mutationFn: async (session: ConfirmationLinkSession): Promise<LoginDestination> => {
      await authApi.adoptSession(session)
      return loginDestination({})
    },
    onSuccess: (destination) => {
      enterDestination(destination, {
        title: m.emailVerified(),
        description: m.successfullySignedIn(),
      })
    },
    onError: () => {
      toast.error(m.confirmationLinkInvalid())
    },
  })

  const { mutate } = adopt
  useEffect(() => {
    if (started.current || link.kind === 'none') return
    started.current = true
    window.history.replaceState(null, '', window.location.pathname + window.location.search)
    if (link.kind === 'failed') {
      toast.error(m.confirmationLinkInvalid())
      return
    }
    mutate(link.session)
  }, [link, mutate])

  return link.kind === 'session' && !adopt.isError
}

export function useUpgradeToBusiness() {
  const queryClient = useQueryClient()
  const router = useRouter()

  return useMutation({
    mutationFn: async () => {
      const { businessId } = await authApi.upgradeToBusiness()
      storageManager.setBusinessId(businessId)

      // business_admin now lives in Supabase app_metadata; refresh so the new
      // access token carries it. Confirm it landed straight from the session
      // (no backend call); a single re-refresh covers propagation lag.
      const readRoles = async () => {
        const { data: sessionData } = await supabase.auth.getSession()
        return (sessionData.session?.user.app_metadata?.roles as string[] | undefined) ?? []
      }

      await supabase.auth.refreshSession()
      if (!(await readRoles()).includes('business_admin')) {
        await new Promise((resolve) => setTimeout(resolve, 400))
        await supabase.auth.refreshSession()
      }

      return { businessId }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKey(QueryKey.AUTH) })
      toast.success(m.accountUpgradedToBusiness())
      router.navigate({ to: '/dashboard' })
    },
    onError: (error) => {
      toast.error(getErrorMessageFromError(error))
    },
  })
}

export function useRegister() {
  const queryClient = useQueryClient()
  const router = useRouter()

  return useMutation({
    mutationFn: async (data: RegisterInput) => {
      const result = await authApi.register(data)
      // Existing account whose password was just verified server-side: sign in
      // (the password is known-correct) so the client holds the upgraded session.
      if (result.upgraded) {
        await authApi.login({ email: data.email, password: data.password })
        await supabase.auth.refreshSession()
      }
      return result
    },
    onSuccess: (result) => {
      if (result.upgraded) {
        queryClient.invalidateQueries({ queryKey: queryKey(QueryKey.AUTH) })
        toast.success(m.accountUpgradedToBusiness())
        router.navigate({ to: '/dashboard' })
        return
      }
      toast.success(m.accountCreated(), {
        description: m.pleaseVerifyYourEmailToContinue(),
      })
    },
    onError: (error) => {
      toast.error(getErrorMessageFromError(error))
    },
  })
}

interface UseLogoutOptions {
  /** Entry page to hard-navigate to after sign-out. */
  redirectTo?: '/login' | '/admin-login'
}

/**
 * Shared sign-out for both portals - clears the Supabase session plus all local
 * business/admin state, then hard-navigates to the given entry page (defaults to
 * the business login).
 */
export function useLogout({ redirectTo = '/login' }: UseLogoutOptions = {}) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      // logout() awaits signOut, which awaits its SIGNED_OUT subscribers, so
      // SessionGate has cleared the app keys by the time this returns.
      await authApi.logout()
      queryClient.clear()
    },
    onSuccess: () => {
      Sentry.setUser(null)
      toast.success(m.signedOutSuccessfully())
      window.location.href = redirectTo
    },
    onError: (error) => {
      toast.error(getErrorMessageFromError(error))
    },
  })
}

export function useAuth() {
  const logout = useLogout()
  const {
    data: user,
    isLoading,
    isAuthenticated,
  } = useRoleAuth({
    queryKey: queryKey(QueryKey.AUTH_CURRENT_USER),
    queryFn: async () => {
      const adminBusiness = storageManager.getAdminViewingBusinessData()
      if (storageManager.isAdmin() && adminBusiness) {
        const {
          data: { session },
        } = await supabase.auth.getSession()
        if (!session?.user) {
          throw new Error('Admin-viewing requires an active session')
        }
        const roles = (session.user.app_metadata?.roles as string[] | undefined) ?? []
        return {
          userId: session.user.id,
          email: session.user.email ?? '',
          businessId: adminBusiness.id,
          roles: roles.join(','),
        }
      }
      const next = await authApi.getCurrentUser()
      storageManager.setBusinessId(next.businessId)
      return next
    },
    enabled: (session) => !!session && sessionHasDashboardRole(session),
    staleTime: 5 * MINUTES,
  })

  return {
    user,
    isAuthenticated,
    isLoading,
    logout: logout.mutate,
  }
}
